/*  iceberg-ui  */
import {
    CardHeading,
    DefaultTableBodyV2,
    SimpleHeaderV2,
    TableV2
} from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Badge,
    Box,
    TableBody,
    TableContainer,
    Typography
} from '@mui/material';
import { Table } from '@tanstack/react-table';

/*  Shared  */
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Internal Components */
import { useManagedTableV2Context } from './ManagedTableV2ContextProvider';
import { ManagedTableAutoupdate } from './internalV2/ManagedTableAutoupdate';
import { ManagedTableCSVExportV2 } from './internalV2/ManagedTableCSVExportV2';
import { ManagedTableFooterV2 } from './internalV2/ManagedTableFooterV2';
import { ManagedTableNewDataV2 } from './internalV2/ManagedTableNewDataV2';
import { ManagedTableSearchBarV2 } from './internalV2/ManagedTableSearchBarV2';
import { ManagedTableShowHideColumns } from './internalV2/ManagedTableShowHideColumns';
import { ManagedTableSkeletonRowsV2 } from './internalV2/ManagedTableSkeletonRowsV2';
import { ManagedTableTogglesV2 } from './internalV2/ManagedTableTogglesV2';
import { ManagedTableViews } from './internalV2/ManagedTableViews';
import { TableFlyout } from './internalV2/TableFlyout';
import {
    ApiCustomTableContext,
    CSVColumn,
    ShowFlyoutMenuActionEnum
} from './types';

export interface TableHeadingProps {
    count: number;
    titleDef: {
        noCount?: boolean;
        subtitle?: React.ReactNode;
        titleKey: string;
    };
}

interface ManagedTableV2UIProps<TData> {
    bulkActions?: React.ReactElement;
    csvBuildOnRequest?: boolean;
    csvData?: TData[];
    csvExport?: boolean;
    csvExportFields?: CSVColumn[];
    csvExportFileName?: string;
    csvIconOnly?: boolean;
    disableRefreshButton?: boolean;
    emptyRowsMessage?: string;
    enableDnD?: boolean;
    enableShowHideColumns?: boolean;
    footer?: React.ReactElement;
    message?: false | React.ReactElement;
    maxExportRows?: number;
    newDataLoading?: boolean;
    noResultsRender?: React.ReactElement;
    onRefresh?: () => void;
    renderFooter?: boolean;
    refreshTooltip?: string;
    renderHeaderRow?: boolean;
    rowHeight?: number;
    titleDef?: {
        noCount?: boolean;
        subtitle?: React.ReactNode;
        titleKey: string;
    };
}

/**
 * Renders a heading for the table with an optional count.
 * @remarks
 * If the `count` prop is not a valid number, the component will handle it as follows:
 * - The `count` value will not be displayed in the `CardHeading` component.
 * - The translated text will use `0` as the count value.
 *
 * @param {Object} props - The component props.
 * @param {number} props.count - The count to display in the heading.
 * @param {string} props.titleKey - The translation key for the title text.
 * @param {boolean} [props.noCount=false] - Whether to hide the count or not.
 * @returns {JSX.Element} The rendered TableHeading component.
 *
 */
export const TableHeading = ({
    count,
    titleDef
}: TableHeadingProps): JSX.Element => {
    const { noCount, subtitle, titleKey } = titleDef;
    const { t } = useTranslation();

    // Check if count is a number
    const isCountValid = typeof count === 'number';
    const doNotShowCount = noCount || !isCountValid;

    return (
        <>
            {titleKey && (
                <CardHeading
                    count={doNotShowCount ? undefined : count}
                    // @ts-expect-error iceberg needs fixing
                    p="16px"
                    pl={doNotShowCount ? '8px' : '16px'}
                >
                    <Typography variant="span" fontSize={FontSizes[3]}>
                        {t(titleKey, { count: isCountValid ? count : 0 })}
                    </Typography>
                </CardHeading>
            )}
            {subtitle && (
                <Box paddingX="16px" paddingY="0">
                    {subtitle}
                </Box>
            )}
        </>
    );
};

// https://github.com/TanStack/table/issues/5026#issuecomment-2734176414
// Whenever we call table.get*row* functions, it's also calling `setPagination`
// which tries to update the state before the table is mounted.
// This is a workaround to make sure that the component is mounted before we call
// the table.get*row* functions.
const ClientOnly = ({ children }: { children: React.ReactNode }) => {
    const [hasMounted, setHasMounted] = useState(false);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    if (!hasMounted) return null;

    return children;
};

/**
 * Renders the UI Table components, table headers, search bar, bulk actions, toggles, skeleton (while loading), body, footer.
 *
 *  @param {Object} props - The props object.
 *  @param {ReactElement} [props.bulkActions] JSX component of buttons that go next to the export button.
 *  @param {boolean} [props.csvBuildOnRequest] If we are to build/retrieve the csv data only after we click on the export to CSV button.
 *  @param {object} [props.csvData] Array of the data that goes in the CSV export.
 *  @param {object} [props.csvExportFields] Array of key(field)/(column-)label objects that fill the columns of the CSV export.
 *  @param {string} [props.csvExportFileName] Addendum to the file name string, in addition to both partner's and client's names and datetime.
 *  @param {boolean} [props.csvIconOnly] Whether the export button shows an icon or the word "Export"
 *  @param {string} [props.emptyRowsMessage] String to show inside an empty table.
 *  @param {boolean} [props.enableDnD] Boolean to allow column drag and drop, if enabled, you will also need the props: tableName and initialState with column visibility and pinning, in/from ManagedTableV2Context.
 *  @param {boolean} [props.enableShowHideColumns] Adds a button to be able to show and hide columns. Needs props: tableName and initialState with column visibility and pinning in/from ManagedTableV2Context. Column defs need the name prop.
 *  @param {ReactElement} [props.footer] JSX component to show at the bottom of the table.
 *  @param {number} [props.maxExportRows] If csvBuildOnRequest is true, then the csv file will only have this maxExportRows.
 *  @param {ReactElement} [props.noResultsRender] JSX component to show instead of an empty table.
 *  @param {boolean} [props.renderFooter] Whether we show the table footer or not, which includes clear filters, footer, and pagination.
 *  @param {boolean} [props.renderHeaderRow] Whether we show the table header row.
 *  @param {number} [props.rowHeight] Number of pixels for the row height.
 *  @param {object} [props.titleDef] If a table has a title at the top. It is a header with or without count. It defines the titleKey, noCount, count, and subtitle.
 *
 *  @returns JSX of the table headers, search bar, bulk actions, toggles, skeleton (while loading), body, footer.
 *
 * @example
 *      <ManagedTableV2UI
 *          renderFooter
 *          titleDef={{
                noCount: true,
                titleKey: 'Common.applications'
            }}
 *      />
 *
 */
export const ManagedTableV2UI = <TData,>({
    bulkActions,
    csvBuildOnRequest,
    csvData,
    csvExport,
    csvExportFields,
    csvExportFileName,
    csvIconOnly,
    disableRefreshButton,
    emptyRowsMessage,
    enableDnD,
    enableShowHideColumns,
    footer,
    message,
    maxExportRows,
    newDataLoading: newDataLoadingProp,
    noResultsRender,
    onRefresh,
    renderFooter,
    refreshTooltip,
    renderHeaderRow,
    rowHeight,
    titleDef = { noCount: false, titleKey: '' }
}: ManagedTableV2UIProps<TData>): React.ReactElement => {
    const {
        apiLoading,
        currentTableView,
        customTableViews,
        externalLoading,
        rowData,
        rowsApi,
        searchEnabled,
        searchText,
        showAutoupdate,
        showFlyoutMenu,
        startPolling,
        table,
        tableViews,
        tableName,
        toggles,
        viewHasBeenModified
    } = useManagedTableV2Context<TData>() as ApiCustomTableContext<TData>;
    const { t } = useTranslation();

    const newDataLoading = newDataLoadingProp || startPolling;

    const showLoadingState =
        (!rowData && Boolean(rowsApi)) || externalLoading || apiLoading;

    if (!table.getRowCount() && noResultsRender) {
        return noResultsRender;
    }

    const showToggles = !!Object.keys(toggles)?.length;

    const disabled = !rowData || externalLoading;

    return (
        <ClientOnly>
            <Box
                data-trackid="managedTableRenderBoxContainer"
                data-testid="managedTableRenderBoxContainer"
                {...(showLoadingState ? { className: 'no-print' } : {})}
            >
                <TableHeading count={table.getRowCount()} titleDef={titleDef} />
                <Box width="100%">
                    <Box
                        display="flex"
                        alignContent="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        width="100%"
                    >
                        {(searchEnabled ||
                            csvExport ||
                            Boolean(bulkActions) ||
                            showAutoupdate) && (
                            <Box
                                alignItems="center"
                                display="flex"
                                flexWrap="wrap"
                                gap="16px"
                                p="16px"
                                width="100%"
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    flexWrap="wrap"
                                    gap="16px"
                                    flex="1"
                                >
                                    {searchEnabled && (
                                        <Box
                                            sx={{
                                                maxWidth: '560px',
                                                width: '560px'
                                            }}
                                        >
                                            <ManagedTableSearchBarV2
                                                disabled={disabled}
                                            />
                                        </Box>
                                    )}
                                    {csvExport && (
                                        <ManagedTableCSVExportV2
                                            csvBuildOnRequest={
                                                !!csvBuildOnRequest
                                            }
                                            csvData={csvData}
                                            csvExport={!!csvExport}
                                            csvExportFields={
                                                csvExportFields ?? []
                                            }
                                            csvExportFileName={
                                                csvExportFileName ?? ''
                                            }
                                            csvIconOnly={!!csvIconOnly}
                                            disabled={!!disabled}
                                            maxExportRows={maxExportRows ?? 0}
                                        />
                                    )}
                                    {bulkActions}
                                </Box>
                                {showAutoupdate && (
                                    <Box justifyContent="flex-end">
                                        <ManagedTableAutoupdate />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {(enableShowHideColumns || !!tableViews) && (
                            <Box
                                alignItems="center"
                                display="flex"
                                gap="16px"
                                paddingX="16px"
                                paddingBottom="16px"
                            >
                                {enableShowHideColumns && (
                                    <ManagedTableShowHideColumns />
                                )}
                                {(!!tableViews || !!customTableViews) && (
                                    <ManagedTableViews
                                        data-testid={`managedTableViews_${tableName}`}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                    {!!tableViews && currentTableView?.displayName && (
                        <Box
                            data-testid="tableViewName"
                            sx={{
                                paddingBottom: '8px',
                                paddingTop: '16px',
                                paddingX: '16px'
                            }}
                        >
                            <DarkTooltip
                                arrow
                                placement="right"
                                title={
                                    viewHasBeenModified &&
                                    t('TableViews.unsavedChanges')
                                }
                            >
                                <Badge
                                    key="badge"
                                    badgeContent="" // If this is null, MUI hides the badge
                                    variant="newDot"
                                    invisible={!viewHasBeenModified}
                                >
                                    <Typography
                                        variant="span"
                                        color="textSecondary"
                                        sx={{
                                            fontSize: FontSizes[3],
                                            marginRight: '12px'
                                        }}
                                    >
                                        {t(currentTableView?.displayName)}
                                    </Typography>
                                </Badge>
                            </DarkTooltip>
                        </Box>
                    )}
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        width="100%"
                        p="16px"
                        pt="0"
                    >
                        {showToggles && (
                            <ManagedTableTogglesV2 disabled={disabled} />
                        )}
                        {(message || newDataLoading) && !showLoadingState && (
                            <ManagedTableNewDataV2
                                customMessage={message}
                                onRefresh={onRefresh}
                                disableRefreshButton={disableRefreshButton}
                                newDataLoading={newDataLoading}
                                refreshTooltip={refreshTooltip}
                            />
                        )}
                    </Box>
                </Box>
                <TableContainer
                    data-trackid="managedTableRenderTableContainer"
                    sx={{ overflow: 'auto' }}
                >
                    <TableV2
                        aria-label="simple table"
                        data-testid="managedTableRenderTable"
                        setColumnOrder={table.setColumnOrder}
                        // @ts-expect-error fix this in iceberg
                        width="100%"
                    >
                        {renderHeaderRow !== false && (
                            <SimpleHeaderV2
                                data-testid="header"
                                enableDnD={enableDnD}
                                // TODO: Fix this in iceberg
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                table={table as any}
                            />
                        )}
                        {showLoadingState ? (
                            <TableBody>
                                <ManagedTableSkeletonRowsV2
                                    rowHeight={rowHeight}
                                />
                            </TableBody>
                        ) : (
                            <DefaultTableBodyV2
                                data-testid="managedTableTableBody"
                                emptyRowsMessage={
                                    /* If we're actively searching the table */
                                    searchEnabled && searchText !== ''
                                        ? t('ManagedTable.noSearchResults')
                                        : emptyRowsMessage ||
                                          t('ManagedTable.noResults')
                                }
                                noRowAlternateColors
                                solidColorRowHover
                                // TODO: Fix this in iceberg
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                table={table as any}
                            />
                        )}
                    </TableV2>
                </TableContainer>
                {!!renderFooter && (
                    <ManagedTableFooterV2
                        disabled={showLoadingState}
                        footer={footer}
                        table={table as Table<TData>}
                    />
                )}
                {!!showFlyoutMenu &&
                    showFlyoutMenu !== ShowFlyoutMenuActionEnum.hide && (
                        <TableFlyout />
                    )}
            </Box>
        </ClientOnly>
    );
};
